import {
  setIsFetching,
  setSearchTargets,
  setSearchTargetsOldVersion,
  setTargetRelations,
  setTargetRelationsOld,
} from '../reducers/searchnFilterReducer';
import { Dispatch } from 'redux';
import SearchnFilterService from 'business/services/searchnFilterService';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ISearchResultDTO,
  ISearchRequestDTO,
  SearchRequestDTO,
  SearchDTO,
  WellIngDTO,
  ISearchFieldDTO,
  FilterDTO,
  IScoresOfWellDropsDTO,
  IFilterOptionsRequestDTO,
  FilterOptionsRequestDTO,
  SearchFieldTypeDTO,
  IWellImageParams,
  ISearchDTO,
  SearchResultDTO,
  IChartDataRequestDTO,
  ChartDataRequestDTO,
} from 'business/models/Models';
import { getModelObject } from 'transformers/utils';
import { IPaginatedList } from 'components/Pagination/constans';
import DropService from 'business/services/dropService';
import { fetchWellImagesBulkAction } from './well';
import { getWellDropImageUniqueKey } from 'redux/utils/well';
import { ScoringWell } from 'business/models/CustomModels';
import { DropDetails } from 'components/well/constants';
import EScoreType from 'enums/EScoreType';
import PlateService from 'business/services/PlateService';
import {
  getWellImageStoreObject,
  setImageLoadingStarted,
} from 'redux/reducers/wellReducer';
import { CrystalSearchTarget } from 'components/searchAndFilter/constants';

const imageFetchingBatchSize = 100;
const MAX_CONCURRENT = 4;
export const fetchSearchTargets = () => async (dispatch: Dispatch) => {
  const dataService = new SearchnFilterService();
  const searchTargets = await dataService.fetchSearchTargets();
  const searchTargetsNew = searchTargets.filter((x) =>
    x.name.includes(CrystalSearchTarget) ? !x.newSearchTarget : x.newSearchTarget,
  );
  const searchTargetsOld = searchTargets.filter((x) => x.newSearchTarget == false);
  const targetRelationsAll = await dataService.fetchTargetRelations();
  const targetRelations: { [key: string]: number[] } = {};
  const targetRelationsOld: { [key: string]: number[] } = {};

  for (const st of searchTargetsNew) {
    if (targetRelationsAll[st.id]) {
      targetRelations[st.id] = targetRelationsAll[st.id];
    }
  }

  for (const st of searchTargetsOld) {
    if (targetRelationsAll[st.id]) {
      targetRelationsOld[st.id] = targetRelationsAll[st.id];
    }
  }

  dispatch(setIsFetching({ value: true, stateKey: 'targetRelations' }));
  dispatch(setSearchTargets(searchTargetsNew));
  dispatch(setTargetRelations(targetRelations));
  dispatch(setSearchTargetsOldVersion(searchTargetsOld));
  dispatch(setTargetRelationsOld(targetRelationsOld));
};

export const fetchWellDropsAction = createAsyncThunk<
  IPaginatedList<WellIngDTO>,
  number[]
>('fechWellDrops', async (wellDropIds, thunkApi) => {
  try {
    const dataService = new SearchnFilterService();
    return await dataService.fetchWellDropsListAsWells(wellDropIds);
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

interface SearchResultThunkParams extends ISearchRequestDTO {
  isWellDropSearch: boolean;
}

export const searchResultThunk = createAsyncThunk<
  ISearchResultDTO,
  SearchResultThunkParams
>('searchResultThunk', async (param, thunkApi) => {
  try {
    const dataService = new SearchnFilterService();
    const paramsModel: SearchRequestDTO = getModelObject(param, SearchRequestDTO);
    const response = await dataService.PerformSearch(paramsModel);

    if (param.isWellDropSearch && response.searchRows.length) {
      /**
       * SNF:RFR: need to verify if we need to fetch ingredients, this will be fetched only if type is welldrop.
       */
      const wellImagesStoreInMemory = getWellImageStoreObject();
      const imageFetchingDetailsList: IWellImageParams[] = [];
      const wellDropIds = [
        ...new Set(
          response.searchRows.map((sr) => {
            const imageParams: IWellImageParams = {
              wellNumber: sr[`Well.Number`],
              dropNumber: sr[`Well Drop.Number`],
              pid: sr[`Experiment Plate.ID`],
              cpID: sr[`Capture Result.Profile ID`],
              batchId: sr[`Capture Result.Image Batch ID`],
              regionID: sr[`Region.ID`],
              thumbnailImageType: 'th',
            };
            imageParams.key = getWellDropImageUniqueKey(imageParams);
            if (
              !wellImagesStoreInMemory.hasOwnProperty(imageParams.key) &&
              !imageParams.key.includes('null')
            ) {
              imageFetchingDetailsList.push(imageParams);
            }

            return sr.__WellDropID;
          }),
        ),
      ];
      thunkApi.dispatch(fetchWellDropsAction(wellDropIds));
      await thunkApi.dispatch(fetchScoresAction(wellDropIds));

      /**
       * call image fetching in batches
       */

      const callFetchImagesInBatches = async () => {
        const batches = [];
        for (
          let i = 0;
          i < imageFetchingDetailsList.length;
          i += imageFetchingBatchSize
        ) {
          batches.push(imageFetchingDetailsList.slice(i, i + imageFetchingBatchSize));
        }

        for (let i = 0; i < batches.length; i += MAX_CONCURRENT) {
          const chunk = batches.slice(i, i + MAX_CONCURRENT);

          await Promise.all(
            chunk.map((batch) => thunkApi.dispatch(fetchWellImagesBulkAction(batch))),
          );
        }
      };
      thunkApi.dispatch(setImageLoadingStarted(imageFetchingDetailsList));
      callFetchImagesInBatches();
    }

    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const fetchSearchDetailsAction = createAsyncThunk<ISearchDTO, number>(
  'fetchSearchDetails',
  async (id: number, thunkApi) => {
    try {
      const dataService = new SearchnFilterService();
      return await dataService.fetchSearchDetails(id);
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

type SaveSearchArgs = {
  searchDto: SearchDTO;
  onFullfill: () => void;
  onError: (error) => void;
};
export const saveSearchAction = createAsyncThunk<boolean, SaveSearchArgs>(
  'saveSearch',
  async (args: SaveSearchArgs, thunkAPI) => {
    try {
      const dataService = new SearchnFilterService();
      const response = await dataService.saveSearch(args.searchDto);
      response && args.onFullfill();
      return response;
    } catch (error) {
      args.onError(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchScoresAction = createAsyncThunk<
  { [key: string]: IScoresOfWellDropsDTO[] },
  number[]
>('fetchScoresByDropIds', async (dropIds: number[], thunkApi) => {
  try {
    const dataService = new DropService();
    return await dataService.getDropScoresByWellDropIds(dropIds);
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const defaultSelectedSearchFieldsThunk = createAsyncThunk<
  { [key: string]: ISearchFieldDTO[] },
  void
>('defaultSelectedSearchFieldsThunk', async (_, thunkApi) => {
  try {
    const dataService = new SearchnFilterService();
    return dataService.getDefaultSelectedSearchFieldsMap();
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const searchFiltersThunk = createAsyncThunk<FilterDTO[], number>(
  'searchFiltersThunk',
  async (searchId, thunkApi) => {
    try {
      const dataService = new SearchnFilterService();
      return await dataService.getSearchFilters(searchId);
    } catch (error) {
      return await thunkApi.rejectWithValue(error);
    }
  },
);

export const searchResultFilterValuesThunk = createAsyncThunk<
  SearchFieldTypeDTO,
  IFilterOptionsRequestDTO
>('searchResultFilterValuesThunk', async (param, thunkApi) => {
  try {
    const dataService = new SearchnFilterService();
    const paramsModel: FilterOptionsRequestDTO = getModelObject(
      param,
      FilterOptionsRequestDTO,
    );
    const response = await dataService.getSearchResultFilterValues(paramsModel);
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const searchRowsCountThunk = createAsyncThunk<ISearchResultDTO, ISearchRequestDTO>(
  'searchRowsCountThunk',
  async (param, thunkApi) => {
    try {
      const dataService = new SearchnFilterService();
      const paramsModel: SearchRequestDTO = getModelObject(param, SearchRequestDTO);
      const response = await dataService.PerformSearch(paramsModel);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const scoreWellsThunk = createAsyncThunk<
  ScoringWell[],
  {
    wellDropScoreId: number;
    wellDropScoreGroupId: number;
    selectedWellDetails: DropDetails[];
    selectedWellScores: IScoresOfWellDropsDTO[][];
  }
>('scoreWellsThunk', async (param, thunkApi) => {
  try {
    const plateService = new PlateService();
    const {
      wellDropScoreId,
      wellDropScoreGroupId,
      selectedWellDetails,
      selectedWellScores,
    } = param;
    const scores: ScoringWell[] = selectedWellDetails.map((selectedWell) => {
      return {
        wellNumber: selectedWell.wellNum,
        plateId: selectedWell.plateID,
        wellDropScoreId,
        wellDropScoreGroupId,
        isAutoScore: false,
        imageBatchId: selectedWell.imageBatchId,
        dropNumber: selectedWell.dropNumber,
        scoreType: EScoreType.Manual,
        isDeleteScore: selectedWellScores
          .flatMap((scores) =>
            scores.filter(
              (s) =>
                s.scoreBatchId === selectedWell.imageBatchId &&
                s.dropNumber === selectedWell.dropNumber &&
                s.wellNumber === selectedWell.wellNum,
            ),
          )
          .some(
            (score) =>
              score.wellDropScoreGroupId === wellDropScoreGroupId &&
              score.scoreType !== EScoreType.Inherited &&
              score.wellDropScoreId === wellDropScoreId,
          ),
      };
    });
    const filteredScoringWells =
      scores.length <= 1 || !scores.some(({ isDeleteScore }) => isDeleteScore)
        ? scores
        : scores.filter(({ isDeleteScore }) => isDeleteScore);
    plateService.setDropScores(filteredScoringWells);
    return filteredScoringWells;
  } catch (error) {
    thunkApi.rejectWithValue(error);
  }
});

export const searchChartDataThunk = createAsyncThunk<
  SearchResultDTO,
  IChartDataRequestDTO
>('searchChartDataThunk', async (param, thunkApi) => {
  try {
    const dataService = new SearchnFilterService();
    const paramsModel: ChartDataRequestDTO = getModelObject(param, ChartDataRequestDTO);
    const response = await dataService.getSearchChartData(paramsModel);
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});
